import { PaperSize } from "building-types/annotation/annotationTypesEnum";
import type { BaseAnnoProps, BaseStyleProps } from "./svgSettings";
import { baseAnnoDefault, baseStyleDefault } from "./svgSettings";
import { sanitizeXmlTextContent } from "./util";

interface DwgScaleOnlyProps extends BaseAnnoProps {
  scaleMultiplier?: number;
  pageSize?: PaperSize;
}

export const MIN_ANNOTATION_SCALE = 0.1;
export const MAX_ANNOTATION_SCALE = 3.0;

export type DwgScaleProps = DwgScaleOnlyProps &
  Pick<
    BaseStyleProps,
    | "fillDarkPrimary"
    | "strokeDarkPrimary"
    | "textFillPrimary"
    | "fillLightPrimary"
    | "mediumFontSize"
    | "regularFontSize"
    | "smallFontSize"
    | "fontFamily0"
  >;

export const dwgScaleDefault: Required<DwgScaleProps> = {
  ...baseStyleDefault,
  ...baseAnnoDefault,
  scaleMultiplier: 48,
  pageSize: PaperSize.SHEET_22X34,
};

export const AVAILABLE_SCALE_FACTORS = {
  4: "3",
  8: "1 1/2",
  12: "1",
  16: "3/4",
  24: "1/2",
  32: "3/8",
  48: "1/4",
  64: "3/16",
  96: "1/8",
  128: "3/32",
  192: "1/16",
};

export function generateDwgScale(userProps: DwgScaleProps): string {
  const props: Required<DwgScaleProps> = { ...dwgScaleDefault, ...userProps };
  let sizeAnnotation = ' @ 22" x 34"';
  let paperScaleFactor = 1;

  if (props.pageSize === PaperSize.SHEET_11X17) {
    sizeAnnotation = ' @ 11" x 17"';
    paperScaleFactor = 2;
  }

  let overallScaleString: string;
  if (props.scaleMultiplier === Number.NaN) {
    overallScaleString = ``;
  } else {
    if (AVAILABLE_SCALE_FACTORS[props.scaleMultiplier]) {
      overallScaleString = `${AVAILABLE_SCALE_FACTORS[props.scaleMultiplier * paperScaleFactor]}" = 1'-0"`;
    } else {
      overallScaleString = `1/${(props.scaleMultiplier * paperScaleFactor) / 12}" = 1'-0"`;
    }
  }

  const scale = props.scale;
  const inverseScale = 1 / scale;

  return `<g transform="translate(${props.x} ${props.y}) scale(${scale} ${scale})">
        <text
          transform="scale(${inverseScale} ${inverseScale})"
          x="-80"
          y="${props.mediumFontSize / 2}"
          fill="${props.textFillPrimary}"
          font-family="${props.fontFamily0}"
          font-size="${props.mediumFontSize}"
          text-anchor="start"
        >${sanitizeXmlTextContent(overallScaleString + sizeAnnotation)}</text>
      </g>`;
}
